import {
  getFeedsComments,
  getFeedsKeyword,
  getFeedsSearchComments,
  getFeedsStats,
  getFeedsCommentsLatestEditLog,
} from '@/pages/opinion/services';
import { SentimentType } from '@op/types/common';
import { arrayToObj } from '@op/utils';
import { useEffect, useState } from 'react';

export function useRequestStats({
  defaultLoading = false,
}: {
  defaultLoading?: boolean;
}) {
  const [feedsStats, setFeedsStats] = useState<{[key: string]: number}>({});
  const [loading, setLoading] = useState(defaultLoading);
  async function requestState(params: any) {
    setLoading(true);
    try {
      const res = await getFeedsStats(params);
      setFeedsStats(res);
    } catch (err) {
      console.error('获取 feeds stats 失败:', err);
      setFeedsStats({});
    } finally {
      setLoading(false);
    }
  }

  return { feedsStats, feedsStatsLoading: loading, requestState };
}

export function useRequestKeyword() {
  const [feedsKeyword, setFeedsKeyword] = useState([]);
  const [loading, setLoading] = useState(false);
  async function requestKeyword(params: any) {
    setLoading(true);
    try {
      const res = await getFeedsKeyword(params);
      setFeedsKeyword(res);
    } catch (err) {
      console.error('获取 feeds keyword 失败:', err);
      setFeedsKeyword([]);
    } finally {
      setLoading(false);
    }
  }

  return { feedsKeyword, feedsKeywordLoading: loading, requestKeyword };
}

export function getLogValueKey(data: any) {
  return data.comment_uin;
}

export function getAllLogValue(
  data: Array<any>,
  callback: { begin?: () => void; end?: () => void } = {},
  params: any = {},
) {
  const comment_uins = (data || []).map((item) => {
    return item.comment_uin;
  });
  if (comment_uins.length === 0) return;
  callback?.begin?.();
  return getFeedsCommentsLatestEditLog({ comment_uins, ...params })
    .then((data) => {
      return arrayToObj(data, getLogValueKey);
    })
    .catch(() => {
      return {};
    })
    .finally(() => {
      callback?.end?.();
    });
}

export function useRequestLatestEditLog({
  data,
  needRequest = true,
  otherParams = {},
  type = ['sentiment', 'topic', 'valid'],
  setApiLoading,
}: {
  data: Array<{ unified_id: string; edition_id: string; comment_uin: string }>;
  needRequest?: boolean;
  otherParams?: any;
  type?: Array<'sentiment' | 'topic' | 'valid'>;
  setApiLoading?: (loading: string) => void;
}) {
  const [logCommentMap, setLogCommentMap] = useState<any>({});
  const [loading, setLoading] = useState(false);

  function requestLogLastEditLog() {
    return getAllLogValue(
      data,
      {
        begin: () => setLoading(true),
        end: () => setLoading(false),
      },
      { ...otherParams, value_type: type },
    )
      ?.then((res) => {
        setLogCommentMap(res);
      })
      .catch(() => {
        setLogCommentMap({});
      })
      .finally(() => {
        setApiLoading?.('comments');
      });
  }

  useEffect(() => {
    if (data.length > 0 && needRequest) {
      requestLogLastEditLog();
    }
  }, [data]);

  return { logCommentMap, loading, requestLogLastEditLog };
}

export function useRequestComments({
  finallyFunc,
}: {
  finallyFunc?: (data: any) => void;
}) {
  const [feedsComments, setFeedsComments] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const [feedsSearchComments, setFeedsSearchComments] = useState<any>({});
  const [feedsSearchCommentsLoading, setFeedsSearchCommentsLoading] =
    useState(false);
  async function requestComments(params: any) {
    setLoading(true);
    setFeedsComments({});
    try {
      const res = await getFeedsComments(params);
      setFeedsComments(res);
      finallyFunc?.(res);
    } catch (err) {
      console.error('获取 comments 失败:', err);
      setFeedsComments({});
    } finally {
      setLoading(false);
    }
  }

  async function requestSearchComments(params: any) {
    setFeedsSearchCommentsLoading(true);
    setFeedsSearchComments({});
    try {
      const res = await getFeedsSearchComments(params);
      setFeedsSearchComments(res || {});
    } catch (err) {
      console.error('获取 search comments 失败:', err);
      setFeedsSearchComments({});
    } finally {
      setFeedsSearchCommentsLoading(false);
    }
  }

  return {
    feedsComments,
    feedsCommentsLoading: loading,
    requestComments,
    requestSearchComments,
    feedsSearchComments,
    feedsSearchCommentsLoading,
  };
}

type LatestKeyType = 'latest_sentiment' | 'latest_topic' | 'latest_valid';

export type logCommentMapType = {
  [key: string]: {
    latest_sentiment: SentimentType;
    latest_topic: string;
    latest_valid: string;
  };
};
export function getLogValue(
  logCommentMap: logCommentMapType,
  data: any,
  type: LatestKeyType,
) {
  const key = getLogValueKey(data);
  const value = logCommentMap?.[key]?.[type];

  return value;
}
